import * as angular from 'angular';
import { Map } from 'immutable';
import { each } from 'lodash';
import { Observable, interval as observableInterval } from 'rxjs';
import { combineLatest, filter, first, map, startWith, switchMap, tap, throttle, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { formatQuery } from '~/lib/query-formatter';
import { WorkspaceSelectors } from '~/selectors/workspace.selectors';
import { AppStore } from '~/types/main.types';

import { Encoding } from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { ChartSwitchActions, TempActions, fromChartSelectors } from '@mode/charts/domain';
import { DeprecatedApiService, QueryResource } from '@mode/legacy/data-access-model-js';
import { QueryState } from '@mode/legacy/types-app-state';
import { MetricFacade, MetricsActions, MetricsHelpers } from '@mode/metrics/domain';
import { ReportEditorFacade } from '@mode/report-editor/domain';
import { QueryResponseTypes, switchViewCache } from '@mode/shared/data-access-webapp';
import { FeatureFlag, SwitchViewResponseTypes } from '@mode/shared/contract-common';
import { getInitialFlagValue, generateUUID, isPresent } from '@mode/shared/util-js';
import { DataViewFacade, SwitchViewActions } from '@mode/switch-views/contract';
import { ReportRunActionType } from '~/actions/report-run.actions';
import { isReportBuilderRoute } from '~/lib/router-helpers';
import { EditorActionType, RunPreviewTableQueryProps } from '../actions/editor-actions';
import { Actions } from '../actions/index';
import { QueryActionType } from '../actions/query.actions';
import { ReportActionType } from '../actions/report-actions';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import { ReportUpdateStrategy } from './../actions/report-actions';
import {
  accountPreferenceSelector,
  modifiedQueriesSelector,
  queryResourcesByKeySelector,
  reportResourcesByKeySelector,
  routerSelector,
  selectedQueryCursorPositionsSelector,
  transientSelectedQueryTokenSelector,
} from './../reducers/selectors';
import { ReportSaveService } from './report-save-service';
import ViewService from './view-service';
import { reportCache } from 'libs/shared/data-access-webapp/src/lib/resource-cache';
import { getEmbeddedCollection } from '@mode/shared/util-halogen';

const createEmpty = () => Promise.resolve();

export class QueryDataService {
  public static $inject = [
    'RxStoreService',
    'actions$',
    'QueryModel',
    'ViewService',
    '$timeout',
    'MetricFacade',
    'AccountFacade',
    'ReportSaveService',
    'ReportEditorFacade',
    'DataViewFacade',
    '$rootScope',
    '$q',
    'ApiService',
    'ModeResource',
  ];

  private isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  constructor(
    private store: AppStore,
    private actions$: Observable<Actions>,
    private QueryModel: any,
    private viewService: ViewService,
    private $timeout: angular.ITimeoutService,
    private metricFacade: MetricFacade,
    private accountFacade: AccountFacade,
    private reportSaveService: ReportSaveService,
    private editorFacade: ReportEditorFacade,
    private dataViewFacade: DataViewFacade,
    private $rootScope: angular.IRootScopeService,
    private $q: angular.IQService,
    private api: DeprecatedApiService,
    private modeResource
  ) {
    // Load Queries from Url Report
    this.actions$
      .pipe(
        ofType(QueryActionType.LoadEmbeddedQueries),
        switchMap((payload) => {
          // Load Queries
          return this.api
            .getResources<QueryResource>(payload.report, this.isExportDatasetsEnabled ? 'local_datasets' : 'queries')
            .then((queries) => this.store.dispatch({ type: QueryActionType.LoadQueriesSuccess, queries: queries }))
            .catch((err) => this.store.dispatch({ type: QueryActionType.LoadQueriesFailure, error: err }));
        })
      )
      .subscribe();

    // Helper double dispatch action to let editor dirty state update
    this.actions$
      .pipe(
        ofType(QueryActionType.UpdateQueryText, QueryActionType.UpdateQueryDatasource),
        map((action) => action.payload),
        tap((payload) =>
          this.store.dispatch({ type: QueryActionType.UpdateQueryState, queryToken: payload.queryToken })
        )
      )
      .subscribe();

    // use report cache to rehydrate query state after refreshEditorViewsSuccess
    this.actions$
      .pipe(
        ofType(SwitchViewActions.refreshEditorViewsSuccess),
        map((action) => {
          const reportCachedResource = reportCache.get(action.reportToken);
          const localDatasets = getEmbeddedCollection<QueryResponseTypes.QueryResource>(
            reportCachedResource,
            'local_datasets',
            'local_datasets'
          );
          if (!localDatasets) return createEmpty();
          const datasetResources = this.buildQueryResourcesFromResponse(localDatasets);
          return this.store.dispatch({
            type: QueryActionType.RefreshQueriesAfterCellExport,
            queries: datasetResources,
          });
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(EditorActionType.UpdateQueryEditor),
        withLatestFrom(this.store.select(transientSelectedQueryTokenSelector)),
        map(([action, queryToken]) => queryToken),
        tap((queryToken) => this.store.dispatch({ type: QueryActionType.UpdateQueryState, queryToken: queryToken }))
      )
      .subscribe();

    // Helper double dispatch action to let editor dirty state update
    this.actions$
      .pipe(
        ofType(QueryActionType.FormatQuerySuccess),
        map((action) => action.payload),
        tap((payload) =>
          this.store.dispatch({ type: QueryActionType.UpdateQueryState, queryToken: payload.query.token })
        )
      )
      .subscribe();

    const createMetricQuery$ = this.actions$.pipe(
      ofType(MetricsActions.createMetricQuery),
      withLatestFrom(
        this.metricFacade.newMetricQueryData$.pipe(filter(isPresent)),
        this.store.select(queryResourcesByKeySelector),
        this.store.select(accountPreferenceSelector)
      ),
      map(
        ([
          ,
          { metricId, metricToken, dataSourceId, dbtSql, vizType, chart, mappingId },
          queryResources,
          accountPreference,
        ]) => {
          const nextCount = queryResources.filter((q) => q.dbt_metric_token).size + 1;
          const query = new this.modeResource({
            name: `Metric ${nextCount}`,
            limit: accountPreference?.sql_editor_preferences?.default_limit_enabled ?? true,
            token: mappingId,
            raw_query: dbtSql,
            data_source_id: dataSourceId,
            _new_query: true,
            created_at: new Date().toISOString(),
            dbt_metric_token: metricToken,
            dbt_metric_id: metricId,
            // @todo: might need to be a chart resource
            // charts: [chart],
          });
          return {
            metricToken,
            vizType,
            chart,
            query,
          };
        }
      )
    );

    // Create new chart for new metric query:
    //  -> report run [success]
    //  -> get mapped query & switch view from run result
    //     1. Swap out tmp. query with query resource
    //     2. Update chart with a real switch_view token
    //     3. Dispatch createNewChart
    createMetricQuery$
      .pipe(
        switchMap((data) =>
          this.actions$.pipe(
            ofType(ReportRunActionType.ExecuteReportRunSuccess),
            map(({ reportRunResource, runningQueryToken }) => {
              // Embedded queries
              const queries = reportRunResource
                .embedded('report')
                .embedded('queries')
                .embedded('queries') as QueryResource[];

              // Embedded local switch views
              const localSwitchViews = reportRunResource
                .embedded('report')
                .embedded('local_switch_views')
                .embedded('switch_views') as SwitchViewResponseTypes.SwitchViewResource[];

              const mappedQuery = queries.find((o) => o.mapping_id === runningQueryToken);
              if (mappedQuery !== undefined) {
                const switchView = localSwitchViews.find((o) => o.source_token === mappedQuery.token);
                if (switchView !== undefined) {
                  let { chart, query } = data;

                  // #1) query is swapped out with the real one
                  query = mappedQuery;

                  // #2) chart needs a switch view token (for ChartSwitchActions.createNewChart)
                  chart = { ...chart, switchViewToken: switchView.token };

                  return {
                    ...data,
                    switchView,
                    query,
                    chart,
                  };
                }
              }
            }),
            first()
          )
        ),
        filter((data) => isPresent(data?.switchView)),
        // @todo/hack: need to prime the switchView cache so we can create a chart
        // for this metric. The switchView is new as of this metric's first run and
        // has not been fetched yet
        tap(({ switchView }) => {
          if (!switchViewCache.has(switchView.token)) {
            switchViewCache.set(switchView.token, switchView);
          }
        })
      )
      .subscribe(({ chart, query }) => {
        this.store.dispatch(
          ChartSwitchActions.createNewChart({
            switchViewToken: chart.switchViewToken,
            queryToken: query.token,
            chartType: chart.chartType,
            encoding: chart.encoding,
            fieldFormats: chart.fieldFormats,
            format: chart.format,
            isAutoUpdateDisabled: chart.isAutoUpdateDisabled,
          })
        );
      });

    createMetricQuery$.subscribe(({ metricToken, vizType, query }) => {
      this.store.dispatch(
        MetricsActions.createMetricQuerySuccess({
          metricToken,
          vizType,
          query,
        })
      );
    });

    this.actions$
      .pipe(
        ofType(QueryActionType.CreateQuery),
        map((action) => action.payload),
        withLatestFrom(this.store.select(queryResourcesByKeySelector), this.store.select(accountPreferenceSelector))
      )
      .subscribe(([{ rawQuery, dataSourceId, queryName }, queryResources, accountPreference]) => {
        const queryNames = Array.from(queryResources.values()).map((query) => query.name);
        const uniqueQueryName = this.getUniqueQueryName(queryNames);
        let query: QueryResource = new this.modeResource({
          name: queryName ?? uniqueQueryName,
          limit: accountPreference?.sql_editor_preferences?.default_limit_enabled ?? true,
          token: generateUUID(),
          raw_query: rawQuery || '',
          data_source_id: dataSourceId || null,
          _new_query: true, // This needed so token can be replaced with saved query token on the backend
          created_at: new Date().toString(),
        });

        this.$timeout(() => {
          this.viewService.selectQuery(query, true);
        });
        this.store.dispatch({ type: QueryActionType.CreateQuerySuccess, payload: query });
      });

    this.actions$
      .pipe(
        ofType(QueryActionType.SaveQuery),
        map((action) => action.payload),
        withLatestFrom(this.store.select(queryResourcesByKeySelector)),
        switchMap(([payload, queryResources]) => {
          let oldToken = payload.query.token;
          let queryResource = queryResources.get(payload.query.token);
          return this.saveOrUpdateQuery(queryResource, payload.reportToken, oldToken);
        }),
        tap((action) => this.store.dispatch(action))
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(QueryActionType.UpdateQuery),
        map((action) => action.payload),
        withLatestFrom(this.store.select(queryResourcesByKeySelector)),
        switchMap(([query, queryResources]) => {
          let queryResource = queryResources.get(query.token);
          return this.saveOrUpdateQuery(queryResource);
        }),
        tap((action) => this.store.dispatch(action))
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(QueryActionType.FormatQuery),
        throttle(() => observableInterval(1000)),
        map((action) => action.payload),
        withLatestFrom(this.store.select(queryResourcesByKeySelector)),
        withLatestFrom(this.store.select(reportResourcesByKeySelector)),
        switchMap(([[payload, queryResources], reportResources]) => {
          let queryState = queryResources.get(payload.queryToken);
          let reportResource = reportResources.get(payload.reportToken);
          return this.handleFormatUnformatQuery(queryState, reportResource);
        }),
        tap((action) => this.store.dispatch(action))
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(QueryActionType.BatchSaveOrUpdateQueryState),
        map((action) => action.reportToken),
        withLatestFrom(this.store.select(modifiedQueriesSelector)),
        switchMap(([reportToken, queryResources]) => {
          let queryPromises = queryResources
            .filter((query) => query.last_run_id)
            .map((query: QueryState) => {
              // TODO: either save or update based on query._new_query. move this to a shared function
              let oldQueryToken = query.token;
              return this.saveOrUpdateQuery(query, reportToken, oldQueryToken, false).then((action) =>
                this.store.dispatch(action)
              );
            });
          return this.$q.all(queryPromises);
        })
      )
      .subscribe();
    // no final do action since we dont care about the resolution of this batch action

    this.actions$
      .pipe(
        ofType(QueryActionType.UpdateQueryName),
        map((action) => action.payload),
        withLatestFrom(
          this.store.select(queryResourcesByKeySelector),
          this.metricFacade.selectedMetric$.pipe(startWith(undefined)),
          this.accountFacade.currentOrg$
        ),
        switchMap(([payload, queryResources, metric, currentOrg]) => {
          const query = queryResources.get(payload.queryToken);
          query.name = payload.queryName;
          if (currentOrg?.features.dbtMetrics && metric) {
            return this.saveOrUpdateQuery(query, '', '', true, metric.token);
          } else {
            return this.saveOrUpdateQuery(query, '', '', true);
          }
        }),
        tap((action) => this.store.dispatch(action))
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(QueryActionType.DestroyQuery),
        map((action) => action.payload),
        withLatestFrom(
          this.store.select(queryResourcesByKeySelector),
          this.store.select(SelectedReportSelectors.getChartTokensByQueryMap),
          this.store.select(SelectedReportSelectors.getTableTokensByQueryMap),
          this.store.select(routerSelector)
        ),
        switchMap(([payload, queryStates, chartTokensByQueryMap, tableTokensByQueryMap, routerState]) => {
          let queryState = queryStates.get(payload.token);
          let queryModel = this.reportSaveService.queries[payload.token];
          let charts = queryModel ? queryModel.storedCharts : [];
          let tables = queryModel ? queryModel.storedTables : [];
          return this.destroyQueryModel(queryState.resource)
            .then(() => {
              // handle legacy state management
              // cleanup local query/chart/table references in ReportSaveService
              this.cleanupReportSaveService(
                queryState.resource,
                charts,
                tables,
                queryStates.map((qs) => qs.resource),
                routerState.name
              );
              const chartTokens = chartTokensByQueryMap.get(queryState.resource.token);
              const tableTokens = tableTokensByQueryMap.get(queryState.resource.token);
              this.store.dispatch({
                type: TempActions.Type.DestroyQuerySuccess,
                token: queryState.resource.token,
                chartTokens,
                tableTokens,
              });
            })
            .catch((err) => this.store.dispatch({ type: QueryActionType.DestroyQueryFailure, error: err }));
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(QueryActionType.UpdateQueryEncoding),
        map((action) => action.payload),
        withLatestFrom(this.metricFacade.selectedMetric$),
        tap(([payload, metric]) => {
          const macroSpec = MetricsHelpers.createMacroSpecFromEncoding(metric, payload.encoding);
          const rawQuery = MetricsHelpers.createDbtSqlFromSpec(macroSpec);
          return this.store.dispatch({
            type: QueryActionType.UpdateQueryEncodingSuccess,
            payload: {
              raw_query: rawQuery,
              queryToken: payload.queryToken,
            },
          });
        })
      )
      .subscribe();

    this.actions$
      .pipe(ofType(TempActions.Type.DestroyQuerySuccess))
      .subscribe((action) => this.dataViewFacade.deleteSwitchViewRef(action.token));
  }

  private saveOrUpdateQuery(
    query: QueryState,
    reportToken?: string,
    oldQueryToken?: string,
    updateName?: boolean,
    metricToken?: string
  ): angular.IPromise<any> {
    const oldQuery = query.resource;
    if (query.resource._new_query) {
      return this.saveQueryModel(query.resource, reportToken)
        .then((res) => {
          // remove any reference that this was a new query
          delete res._resource._new_query;
          // legacy support for old report state service for query management
          // TODO: remove this when fully on rxStore query state
          delete this.reportSaveService.queries[oldQueryToken];
          this.reportSaveService.queries[res.token] = res;
          this.viewService.selectQuery(res, false);
          return { type: QueryActionType.SaveQuerySuccess, payload: { query: res._resource, oldToken: oldQueryToken } };
        })
        .catch((err) => ({ type: QueryActionType.SaveQueryFailure, error: err }));
    } else {
      let updated = query.resource.generate<QueryResource>({
        ...query.resource,
        raw_query: query.raw_query,
        data_source_id: query.data_source_id,
        name: query.name,
        limit: query.limit,
        charts: query.charts,
      });
      return this.updateQueryModel(updated)
        .then((res) => {
          // support old world state for now
          // TODO: remove this or move to rxStore state
          this.reportSaveService.queries[res.token] = res;
          let payload = res._resource;
          if (metricToken) {
            payload = {
              ...payload,
              metric_token: metricToken,
            };
          }
          if (updateName) {
            this.$rootScope.$broadcast('preview.update', {
              updateQuery: {
                token: res.token,
                name: res.name,
              },
            });
          }
          return { type: QueryActionType.UpdateQuerySuccess, payload };
        })
        .catch((err) => ({ type: QueryActionType.UpdateQueryFailure, error: err }));
    }
  }

  private saveQueryModel(query: QueryResource, reportToken: string): angular.IPromise<any> {
    let oldQuery = this.reportSaveService.queries[query.token];
    // ignore embedded objects from resource since old model layer will reinitialize these
    // when newing up resource by using `ignoreEmbedded: true`
    let queryModel = new this.QueryModel.model(query, { ignoreEmbedded: true });
    // some legacy stuff to support the old queries collection model
    // TODO: remove when we no longer need to rely on queries collection
    if (oldQuery) {
      oldQuery.extendAttributes(queryModel);
      queryModel = oldQuery;
    }
    let params = queryModel.toParams();
    params.includes = ['charts', 'query_tables'];
    return this.QueryModel.create(
      {
        report: reportToken,
        params,
      },
      queryModel
    );
  }

  private updateQueryModel(query: QueryResource): angular.IPromise<any> {
    // ignore embedded objects from resource since old model layer will reinitialize these
    // when newing up resource by using `ignoreEmbedded: true`
    let queryModel = new this.QueryModel.model(query, { ignoreEmbedded: true });
    return this.QueryModel.update(queryModel);
  }

  private destroyQueryModel(query: QueryResource): angular.IPromise<any> {
    return new this.QueryModel.model(query, { ignoreEmbedded: true }).destroy();
  }

  private handleFormatUnformatQuery(query: QueryState, report: any): angular.IPromise<Actions> {
    let action: Actions = null; // So the resolved action is typesafe

    if (this.formatUndoable(query)) {
      action = {
        type: QueryActionType.FormatQuerySuccess,
        payload: {
          query: query.resource,
          formatted: false,
          raw_query: query.original_query,
        },
      };
      // handle unformat
      return this.$q.resolve(action);
    } else if (this.formatCached(query)) {
      // handle format from cache
      action = {
        type: QueryActionType.FormatQuerySuccess,
        payload: {
          query: query.resource,
          formatted: true,
          raw_query: query.formatted_query,
        },
      };
      return this.$q.resolve(action);
    } else {
      // make format backend call
      const original_query = query.raw_query;

      const formatted = formatQuery(query.raw_query);
      action = {
        type: QueryActionType.FormatQuerySuccess,
        payload: {
          query: query.resource,
          formatted: true,
          raw_query: formatted,
          original_query,
          formatted_query: formatted,
        },
      };

      return this.$q.resolve(action);
    }
  }

  private formatCached(query: QueryState): boolean {
    return query.formatted_query && query.original_query && query.original_query === query.raw_query;
  }

  private getUniqueQueryName(queryNameArray: string[]): string {
    const maxNumber = queryNameArray.reduce((max: number, name: string) => {
      if (name.startsWith('Query ')) {
        const num = parseInt(name.slice(6), 10);
        return num > max ? num : max;
      }
      return max;
    }, 0);
    return `Query ${maxNumber + 1}`;
  }

  public formatUndoable(query: QueryState): boolean {
    if (query.original_query === query.formatted_query && query.formatted === false) {
      // deal with stupid case where the format didn't actually change the query
      return false;
    } else {
      return query.original_query && query.formatted_query && query.formatted_query === query.raw_query;
    }
  }

  private validateFormatErrorMessage(e) {
    if (e.status == 400) {
      return 'Sorry, there is a problem with your query.  Please check it and try again.';
    } else if (e.status == 404) {
      return 'Sorry, there is a problem with your query. Please check it and try again.';
    } else if (e.status == 413) {
      return 'Sorry, the query is too big.';
    } else if (e.status == 429 || e.status == 503) {
      return 'Sorry, the formatter is busy.  Please try again later.';
    } else {
      return 'Sorry, something went wrong. Please try again.';
    }
  }

  // Legacy cleanup helper function
  // TODO: move cleanup of tables and charts to effects from destroy query action
  private cleanupReportSaveService(
    query: QueryResource,
    charts: any[],
    tables: any[],
    queries: Map<string, QueryResource>,
    currentRouteName: string
  ): void {
    // destroy any charts
    each(charts, (chart) => {
      this.reportSaveService.removeChart(chart.token);
      chart.destroy({ silent: true });
    });
    // destroy any tables
    each(tables, (table) => {
      this.reportSaveService.removeTable(table.token);
      table.destroy({ silent: true });
    });

    // remove query references
    this.reportSaveService.removeQuery(query.token);

    this.$timeout(() => {
      let selectedQuery = this.viewService.views.selectedQuery;
      let queriesArr = queries
        .filter((q) => q.token !== query.token)
        .valueSeq()
        .toArray();

      if (selectedQuery && (selectedQuery.token === query.token || query.mapping_id === selectedQuery.token)) {
        if (queries.size && queriesArr.length) {
          const isNonReportBuilderRoute = !isReportBuilderRoute(currentRouteName); // Prevent editor-builder-view from going invisible while in report builder.
          this.viewService.selectQuery(queries.get(queriesArr[0].token), isNonReportBuilderRoute);
        }
      }

      this.store.dispatch({
        type: ReportActionType.UpdateReport,
        payload: {
          token: this.reportSaveService.report.token,
          strategy: ReportUpdateStrategy.SilentAndRefreshPreview,
        },
      });
    });
  }

  public get editorSelectedQuery$() {
    return this.store.select(WorkspaceSelectors.getEditorSelectedQuery);
  }

  public get editorQueriesAndSelected$() {
    return this.store.select('client', 'selectedReport', 'queries').pipe(
      combineLatest(this.editorSelectedQuery$, (queries, { token }) => {
        return {
          queries,
          selectedToken: token,
          selectedQuery: queries.getRecord(token),
        };
      })
    );
  }

  public get queriesAndSelected$() {
    return this.store.select('client', 'selectedReport', 'queries').pipe(
      combineLatest(this.store.select(transientSelectedQueryTokenSelector), (queries, token) => {
        return {
          queries,
          selectedToken: token,
          selectedQuery: queries.getRecord(token),
        };
      })
    );
  }

  public get metricQueryVizTokens$(): Observable<{ [queryToken: string]: string }> {
    // this should probably be a selector
    const metricQueries$: Observable<string[]> = this.store.selectNoEmpty(SelectedReportSelectors.getQueries).pipe(
      // filter for dbt metric queries
      map((queries) => queries.byKey.filter((q) => !!q.dbt_metric_token)),
      // extract query tokens
      map((queries) => Array.from(queries.keys()))
    );

    return metricQueries$.pipe(
      withLatestFrom(this.store.selectNoEmpty(fromChartSelectors.selectQueryTokens)),
      map(function mapChartTokensToQueryTokens([queries, chartTokensByQueryToken]) {
        return queries.reduce((accu, queryToken) => {
          // metric queries only have one chart so map the first chart that's found
          accu[queryToken] = chartTokensByQueryToken.findKey((query) => query === queryToken);
          return accu;
        }, {});
      })
    );
  }

  // Used for populating the report browser views in the old world. If that ever gets refactored this should be replaced.
  public get storedChartsAndTables$() {
    return this.store.select(SelectedReportSelectors.getChartsByQueries).pipe(
      map((charts) => {
        let storedCharts = {};
        charts.forEach((chartList, queryToken) => {
          storedCharts[queryToken] = chartList.map((c) => c.resource).toArray();
        });

        return storedCharts;
      }),
      combineLatest(
        this.store.select('client', 'selectedReport', 'tablesByQuery').pipe(
          withLatestFrom(this.store.select('client', 'selectedReport', 'tables'), function (tablesByQuery, tables) {
            let storedTables = {};
            tablesByQuery.forEach((tableKeys, key) => {
              storedTables[key] = tables.getRecords(tableKeys.toArray());
            });

            return storedTables;
          })
        ),
        (charts, tables) => {
          return {
            storedTables: tables,
            storedCharts: charts,
          };
        }
      )
    );
  }

  public createQuery() {
    this.store.dispatch({
      type: QueryActionType.CreateQuery,
      payload: {},
    });
  }

  public createQueryFromExisting(rawQuery: string, dataSourceId: string, queryName?: string) {
    this.store.dispatch({
      type: QueryActionType.CreateQuery,
      payload: { rawQuery, dataSourceId, queryName },
    });
  }

  public saveQuery(query: any, reportToken: string) {
    this.store.dispatch({
      type: QueryActionType.SaveQuery,
      payload: { query, reportToken },
    });
  }

  public updateQuery(queryModel: any) {
    this.store.dispatch({
      type: QueryActionType.UpdateQuery,
      payload: queryModel,
    });
  }

  public updateQueryText(queryToken: string, queryText: string) {
    this.store.dispatch({
      type: QueryActionType.UpdateQueryText,
      payload: {
        queryToken,
        queryText,
      },
    });
  }

  public updateQueryName(queryToken: string, queryName: string) {
    this.store.dispatch({
      type: QueryActionType.UpdateQueryName,
      payload: {
        queryToken,
        queryName,
      },
    });
  }

  public updateQueryLimit(queryToken: string, queryLimit: boolean) {
    this.store.dispatch({
      type: QueryActionType.UpdateQueryLimit,
      payload: {
        queryToken,
        queryLimit,
      },
    });
  }

  public updateQueryDatasource(queryToken: string, queryDatasource: string) {
    this.store.dispatch({
      type: QueryActionType.UpdateQueryDatasource,
      payload: {
        queryToken,
        queryDatasource,
      },
    });
  }

  public destroyQuery(queryToken: string) {
    this.store.dispatch({
      type: QueryActionType.DestroyQuery,
      payload: {
        token: queryToken,
      },
    });
  }

  public batchSaveOrUpdateQueryState(reportToken: string) {
    this.store.dispatch({
      type: QueryActionType.BatchSaveOrUpdateQueryState,
      reportToken,
    });
  }

  public batchUpdateQueries(queries: QueryResource[]) {
    // Get selected query token and check if it needs to be remapped
    this.editorFacade.selectedQueryToken$.pipe(first()).subscribe((queryToken) => {
      this.store.dispatch({
        type: QueryActionType.BatchUpdateQueries,
        payload: queries,
      });

      const remappedQuery = queries.find((q) => q.mapping_id === queryToken);
      if (remappedQuery !== undefined) {
        this.editorFacade.selectQuery(remappedQuery.token);
      }
    });
  }

  public loadQueries(queryModels: any[]) {
    this.store.dispatch({
      type: QueryActionType.LoadQueriesSuccess,
      queries: queryModels,
    });
  }

  public formatQuery(queryToken: string, reportToken: string) {
    this.store.dispatch({
      type: QueryActionType.FormatQuery,
      payload: {
        queryToken,
        reportToken,
      },
    });
  }

  public updateQueryEditor(text: string, selectionLead: number, selectionAnchor: number) {
    this.store.dispatchThunk(transientSelectedQueryTokenSelector, (selectedQueryToken) => ({
      type: EditorActionType.UpdateQueryEditor,
      payload: {
        text,
        selectionLead,
        selectionAnchor,
        selectedQueryToken,
      },
    }));
  }

  public updateQueryEncoding(queryToken: string, reportToken: string, encoding: Encoding) {
    this.store.dispatch({
      type: QueryActionType.UpdateQueryEncoding,
      payload: {
        queryToken,
        reportToken,
        encoding,
      },
    });
  }

  public insertQueryText(text: string) {
    this.store.dispatchThunk(selectedQueryCursorPositionsSelector, ({ selectedQueryToken, selectionStart }) => ({
      type: EditorActionType.InsertQueryText,
      payload: {
        text,
        selectedQueryToken,
        selectionStart,
      },
    }));
  }

  public runPreviewTableQuery(queryProps: RunPreviewTableQueryProps) {
    const { tableSchema, tableName, tableColumns, buttonLocation, searchQuery } = queryProps;
    this.store.dispatchThunk(transientSelectedQueryTokenSelector, (selectedQueryToken) => ({
      type: EditorActionType.RunPreviewTableQuery,
      payload: {
        tableSchema,
        tableName,
        tableColumns,
        buttonLocation,
        searchQuery,
        selectedQueryToken,
      },
    }));
  }

  private buildQueryResourcesFromResponse(queryResp: QueryResponseTypes.QueryResource[]): QueryResource[] {
    return queryResp.map(
      (query) =>
        new this.modeResource({
          token: query.token,
          raw_query: query.raw_query,
          created_at: query.created_at,
          limit: query.limit,
          name: query.name,
          last_run_id: query.last_run_id,
          data_source_id: query.data_source_id,
          explorations_count: query.explorations_count,
          report_imports_count: query.report_imports_count,
          _new_query: query.raw_query,
          _embedded: query._embedded,
          _forms: query._forms,
          _links: query._links,
        })
    );
  }
}
